import React, { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import EntitySearchSelect from "./EntitySearchSelect";
import { Button } from "../ui/button";
import { Loader2, GitMerge, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Merge one firm INTO another.  All loans + users move to the target firm,
 * the source is soft-deleted.  Used to dedupe accidental duplicate firm
 * entries that happen when two attorneys with different spellings created
 * separate firm records.
 */
export default function MergeFirmModal({ source, onClose, onMerged }) {
  const [target, setTarget] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!target || target.firm_id === source.firm_id) {
      toast.error("Pick a different target firm.");
      return;
    }
    if (confirmText.trim().toLowerCase() !== "merge") return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/operations/firms/merge", { source_firm_id: source.firm_id, target_firm_id: target.firm_id });
      toast.success(`Merged · ${data.loans_moved} loans + ${data.users_moved} users moved into ${target.name}.`);
      onMerged && onMerged(data);
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0A2540]/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-5" data-testid="merge-firm-modal">
      <form onSubmit={submit} className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg p-6 sm:p-7">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6B7280] mb-2">
              <GitMerge className="w-3.5 h-3.5" /> Merge firm
            </div>
            <h2 className="font-display text-xl text-[#0A2540] font-semibold">Merge {source.name}</h2>
            <p className="text-xs text-[#6B7280] mt-1">All {source.loan_count || 0} loans and {source.user_count || 0} users will move into the target. The source firm will be archived.</p>
          </div>
          <button type="button" onClick={onClose} className="text-[#9CA3AF] hover:text-[#0A2540]" data-testid="merge-firm-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-[#6B7280] font-medium">Target firm (loans + users move here)</label>
            <div className="mt-1.5">
              <EntitySearchSelect
                kind="firm"
                value={target?.name || ""}
                onSelect={(row) => row.firm_id !== source.firm_id && setTarget(row)}
                testidPrefix="mf-target"
              />
            </div>
            {target && (
              <div className="mt-2 text-xs text-[#047857]">Selected: <span className="font-medium">{target.name}</span> ({target.loan_count} loan{target.loan_count !== 1 ? "s" : ""})</div>
            )}
          </div>

          <div className="bg-[#FEF3C7] border border-[#FDE68A] text-[#92400E] text-xs px-3 py-2.5 rounded-md flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>This is reversible only via individual loan reassignment. Domains from both firms will be combined. Type <strong>merge</strong> below to confirm.</span>
          </div>

          <div>
            <label className="text-xs text-[#6B7280] font-medium">Type <span className="font-mono text-[#0A2540]">merge</span> to confirm</label>
            <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="w-full h-10 px-3 rounded-md border border-[#E5E7EB] text-sm mt-1.5 focus:outline-none focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]" data-testid="mf-confirm" />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={submitting || !target || confirmText.trim().toLowerCase() !== "merge"}
            className="bg-[#0066FF] hover:bg-[#0052CC]" data-testid="mf-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <GitMerge className="w-4 h-4 mr-1.5" />} Merge firms
          </Button>
        </div>
      </form>
    </div>
  );
}
